import React, {useMemo, useState} from "react";
import {Alert, Button, Card, Empty, Form, Input, Modal, Space, Tag, Tooltip, Typography, message} from "antd";
import {DeleteOutlined, KeyOutlined, PlusOutlined, SafetyCertificateOutlined} from "@ant-design/icons";
import {
    useCreateSupervisor,
    useDeleteSupervisor,
    useSetSupervisorPassword,
    useSupervisors,
} from "./operators-query";

const {Text, Paragraph} = Typography;

/**
 * Supervisor accounts.
 *
 * <p>Its own section rather than a role column on the operator table, because a supervisor is a
 * different kind of account and not an operator with extra rights: no profile row, no shift, no
 * caseload. They see every conversation on the desk, can read any of them, step in and answer, hand
 * one to another operator and close one — and cannot touch widget settings, accounts, passwords or
 * deletion.
 *
 * <p>There is deliberately no shift editor here. A shift is what puts somebody in the assignment
 * rota, and a supervisor being handed conversations to answer is the one thing this role must not
 * become — they watch the queue rather than stand in it.
 */
export default function SupervisorsSection() {
    const {data, isLoading} = useSupervisors();
    const supervisors = useMemo(() => data?.data || data || [], [data]);

    const [addOpen, setAddOpen] = useState(false);
    const [pwdTarget, setPwdTarget] = useState(null);
    const [form] = Form.useForm();
    const [pwdForm] = Form.useForm();

    const {mutate: create, isPending: creating} = useCreateSupervisor(() => {
        message.success("حساب سرپرست ساخته شد.");
        form.resetFields();
        setAddOpen(false);
    });

    const {mutate: setPwd, isPending: settingPwd} = useSetSupervisorPassword(() => {
        message.success("رمز عبور تغییر کرد.");
        pwdForm.resetFields();
        setPwdTarget(null);
    });

    const {mutate: removeSupervisor} = useDeleteSupervisor(() =>
        message.success("حساب سرپرست حذف شد.")
    );

    // The backend's own reason is the useful one ("at least 8 characters", "cannot be the email",
    // "an account with this address already exists"); a generic failure would hide all three.
    const reportFailure = (err) =>
        message.error(
            err?.response?.data?.message || err?.response?.data?.error || "انجام نشد.",
            6
        );

    const notTheEmail = (readEmail) => ({
        validator(_, value) {
            const email = (readEmail() || "").trim().toLowerCase();
            const local = email.includes("@") ? email.split("@")[0] : email;
            const typed = (value || "").trim().toLowerCase();
            if (typed && email && (typed === email || typed === local)) {
                return Promise.reject(new Error("رمز عبور نمی‌تواند همان ایمیل باشد"));
            }
            return Promise.resolve();
        },
    });

    const confirmRemove = (s) =>
        Modal.confirm({
            title: "حذف حساب سرپرست",
            content: "حساب " + s.username + " حذف شود؟ گفت‌وگوها دست‌نخورده می‌مانند.",
            okText: "بله، حذف شود",
            cancelText: "انصراف",
            okButtonProps: {danger: true},
            onOk: () => removeSupervisor(s.id),
        });

    return (
        <Card
            size="small"
            style={{marginTop: 16}}
            title={
                <Space>
                    <SafetyCertificateOutlined/>
                    <span>سرپرست‌ها</span>
                </Space>
            }
            extra={
                <Button type="primary" icon={<PlusOutlined/>} onClick={() => setAddOpen(true)}>
                    افزودن سرپرست
                </Button>
            }
            loading={isLoading}
        >
            <Paragraph type="secondary" style={{fontSize: 12, marginBottom: 12}}>
                سرپرست همه‌ی گفت‌وگوهای زنده را می‌بیند، می‌تواند بخواند، خودش پاسخ بدهد، گفت‌وگو را به
                اپراتور دیگری بسپارد و ببندد. برنامهٔ کاری اپراتورها را هم تعیین می‌کند — تنها چیزی که
                دربارهٔ اپراتورها می‌تواند تغییر دهد. ساختن و حذف اپراتور، تعیین رمز، تنظیمات ویجت و حذف
                گفت‌وگو دست او نیست. خودش شیفت ندارد، چون قرار نیست در نوبت پاسخ‌گویی قرار بگیرد.
            </Paragraph>

            {supervisors.length === 0 ? (
                <Empty description="هنوز سرپرستی تعریف نشده" image={Empty.PRESENTED_IMAGE_SIMPLE}/>
            ) : (
                supervisors.map((s) => (
                    <div
                        key={s.id}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            padding: "8px 0",
                            borderBottom: "1px solid var(--npe-hairline)",
                        }}
                    >
                        <div style={{minWidth: 0}}>
                            <div style={{fontWeight: 500}}>{s.fullName || "—"}</div>
                            <Text type="secondary" style={{fontSize: 12}} dir="ltr">
                                {s.username}
                            </Text>
                            {s.mustChangePassword && (
                                <Tag color="orange" style={{marginInlineStart: 8, fontSize: 11}}>
                                    هنوز رمز اولیه
                                </Tag>
                            )}
                        </div>
                        <Space size={4}>
                            <Tooltip title="تغییر رمز عبور">
                                <Button size="small" icon={<KeyOutlined/>} onClick={() => setPwdTarget(s)}/>
                            </Tooltip>
                            <Tooltip title="حذف">
                                <Button size="small" danger icon={<DeleteOutlined/>}
                                        onClick={() => confirmRemove(s)}/>
                            </Tooltip>
                        </Space>
                    </div>
                ))
            )}

            <Modal
                open={addOpen}
                title="افزودن سرپرست"
                onCancel={() => setAddOpen(false)}
                onOk={() => form.submit()}
                okText="ساختن حساب"
                cancelText="انصراف"
                confirmLoading={creating}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    onFinish={(v) =>
                        create(
                            {
                                username: (v.username || "").trim(),
                                fullName: (v.fullName || "").trim(),
                                initialPassword: v.initialPassword,
                            },
                            {onError: reportFailure}
                        )
                    }
                >
                    <Form.Item
                        name="fullName"
                        label="نام و نام خانوادگی"
                        rules={[{required: true, message: "نام را وارد کنید"}]}
                    >
                        <Input/>
                    </Form.Item>

                    <Form.Item
                        name="username"
                        label="ایمیل (نام کاربری ورود)"
                        rules={[
                            {required: true, message: "ایمیل را وارد کنید"},
                            {type: "email", message: "ایمیل نامعتبر است"},
                        ]}
                    >
                        <Input dir="ltr" autoComplete="off"/>
                    </Form.Item>

                    <Form.Item
                        name="initialPassword"
                        label="رمز عبور اولیه"
                        tooltip="از راهی امن به سرپرست بدهید؛ در اولین ورود از او خواسته می‌شود آن را تغییر دهد."
                        rules={[
                            {required: true, message: "رمز عبور اولیه را وارد کنید"},
                            {min: 8, message: "حداقل ۸ کاراکتر"},
                            ({getFieldValue}) => notTheEmail(() => getFieldValue("username")),
                        ]}
                    >
                        <Input.Password autoComplete="new-password"/>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={!!pwdTarget}
                title="تغییر رمز عبور سرپرست"
                onCancel={() => setPwdTarget(null)}
                onOk={() => pwdForm.submit()}
                okText="ذخیره رمز جدید"
                cancelText="انصراف"
                confirmLoading={settingPwd}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    style={{marginBottom: 16}}
                    message={pwdTarget?.fullName || pwdTarget?.username}
                    description="رمز جدید را از راهی امن به او بدهید. این رمز دیگر جایی نمایش داده نمی‌شود."
                />
                <Form
                    form={pwdForm}
                    layout="vertical"
                    requiredMark={false}
                    onFinish={(v) =>
                        setPwd({id: pwdTarget.id, newPassword: v.newPassword}, {onError: reportFailure})
                    }
                >
                    <Form.Item
                        name="newPassword"
                        label="رمز عبور جدید"
                        rules={[
                            {required: true, message: "رمز عبور جدید را وارد کنید"},
                            {min: 8, message: "حداقل ۸ کاراکتر"},
                            notTheEmail(() => pwdTarget?.username),
                        ]}
                    >
                        <Input.Password autoComplete="new-password" autoFocus/>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
}
